import React, { useState, useEffect, useRef } from 'react';
import Select, { createFilter } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import './App.css';

// --- 型定義 ---
interface SubmissionData {
  send_select: string;
  name: string;
  email: string;
  work_date: string;
  postal_code: string;
  prefecture: string;
  city: string;
  address1: string;
  address2: string;
  bankCheck: boolean;
  bankinfo1: string; // Bank Name
  bankinfo2: string; // Branch Code
  bankinfo_branch_name: string; // Branch Name
  bankinfo3: string; // Deposit Type
  bankinfo4: string; // Account Number
  bankinfo5: string; // Account Name
  registStatus: boolean;
  project_no?: string;
  work_time?: string;
  work_info?: string;
  work_content?: string;
  work_time_actual?: string;
}

interface AddressCandidate {
  address1: string; // 都道府県
  address2: string; // 市区町村
  address3: string; // 町域名
  kana1: string;
  kana2: string;
  kana3: string;
  prefcode: string;
  zipcode: string;
}

interface OptionType {
  value: string;
  label: string;
}

type ErrorRecord = Partial<Record<keyof SubmissionData | 'isBranchConfirmed', string>>;

const prefectures = [
  "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県",
  "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
  "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県", "岐阜県",
  "静岡県", "愛知県", "三重県", "滋賀県", "京都府", "大阪府", "兵庫県",
  "奈良県", "和歌山県", "鳥取県", "島根県", "岡山県", "広島県", "山口県",
  "徳島県", "香川県", "愛媛県", "高知県", "福岡県", "佐賀県", "長崎県",
  "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県"
];

const App: React.FC = () => {
  // --- State Hooks ---
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [workDate, setWorkDate] = useState('');
  const [mode, setMode] = useState('');
  const [details, setDetails] = useState<Partial<SubmissionData>>({});
  const [bankOptions, setBankOptions] = useState<OptionType[]>([]);
  const [addressCandidates, setAddressCandidates] = useState<AddressCandidate[]>([]);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isDetailFormVisible, setIsDetailFormVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPostalLoading, setIsPostalLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [errors, setErrors] = useState<ErrorRecord>({});
  const [selectedBankCode, setSelectedBankCode] = useState('');
  const [currentBranches, setCurrentBranches] = useState<any>(null);
  const [isBranchAutoFilled, setIsBranchAutoFilled] = useState(false);
  const [isBranchConfirmed, setIsBranchConfirmed] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isFinalVerified, setIsFinalVerified] = useState(false);

  const API_BASE_URL = '/api';
  const popupRef = useRef<HTMLDivElement>(null);
  const isInitialLoadForFoundUser = useRef(false);

  // --- Effects ---
  useEffect(() => {
    const loadBanks = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/banks`);
        if (!response.ok) throw new Error('銀行リストの取得に失敗しました。');
        const data = await response.json();
        
        // データの形式を判定して柔軟に処理
        const options = data.map((item: any) => {
          if (Array.isArray(item) && item.length >= 2) {
            // [名称, コード] の形式を想定。表示は「コード: 名称」
            return { value: item[1] || '', label: item[0] || '', fullLabel: `${item[1]} ${item[0]}` };
          } else if (Array.isArray(item) && item.length === 1) {
            return { value: item[0] || '', label: item[0] || '', fullLabel: item[0] };
          }
          return { value: item || '', label: item || '', fullLabel: item };
        });
        
        setBankOptions(options);
      } catch (error: any) {
        setMessage({ type: 'danger', text: error.message });
      }
    };
    loadBanks();
  }, []);

  useEffect(() => {
    const fetchAddress = async () => {
      if (isInitialLoadForFoundUser.current) {
        isInitialLoadForFoundUser.current = false;
        return;
      }
      if (details.postal_code && details.postal_code.length === 7) {
        setIsPostalLoading(true);
        try {
          const response = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${details.postal_code}`);
          const data = await response.json();
          if (data.status === 200 && data.results) {
            setAddressCandidates(data.results);
            setIsPopupVisible(true);
            setMessage({ type: '', text: '' });
          } else {
            setAddressCandidates([]);
            setMessage({ type: 'warning', text: `郵便番号に該当する住所が見つかりません: ${data.message || ''}` });
          }
        } catch (error) {
          setMessage({ type: 'danger', text: '住所の取得に失敗しました。' });
        } finally {
          setIsPostalLoading(false);
        }
      }
    };
    fetchAddress();
  }, [details.postal_code]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setIsPopupVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- 支店リストの一括取得 (銀行選択時) ---
  useEffect(() => {
    const fetchBranches = async () => {
      if (selectedBankCode && /^\d{4}$/.test(selectedBankCode)) {
        try {
          // zengin-code.jp の API 形式 (GitHub Pages 版)
          const response = await fetch(`https://zengin-code.github.io/api/branches/${selectedBankCode}.json`);
          if (response.ok) {
            const data = await response.json();
            setCurrentBranches(data); // { "001": { "name": "本店", ... }, ... }
          }
        } catch (error) {
          console.error("支店リストの取得に失敗しました:", error);
          setCurrentBranches(null);
        }
      } else {
        setCurrentBranches(null);
      }
    };
    fetchBranches();
  }, [selectedBankCode]);

  // --- 支店コード入力時の自動補完 ---
  useEffect(() => {
    if (currentBranches && details.bankinfo2 && details.bankinfo2.length === 3) {
      const branchInfo = currentBranches[details.bankinfo2];
      if (branchInfo && branchInfo.name) {
        setDetails(prev => ({ ...prev, bankinfo_branch_name: branchInfo.name }));
        setIsBranchAutoFilled(true);
        setIsBranchConfirmed(false); // 自動補完されたら再確認を促す
        if (errors.bankinfo_branch_name) {
          setErrors(prev => ({ ...prev, bankinfo_branch_name: undefined }));
        }
      }
    }
  }, [currentBranches, details.bankinfo2]);

  // --- Validation ---
  const validate = (): ErrorRecord => {
    const newErrors: ErrorRecord = {};
    if (!details.postal_code) newErrors.postal_code = '郵便番号を入力してください。';
    else if (details.postal_code.length !== 7) newErrors.postal_code = '郵便番号は7桁で入力してください。';
    if (!details.prefecture) newErrors.prefecture = '都道府県を選択してください。';
    if (!details.city) newErrors.city = '市区町村を入力してください。';
    if (!details.address1) newErrors.address1 = '町名・番地を入力してください。';

    if (details.bankCheck) {
      if (!details.bankinfo1) newErrors.bankinfo1 = '銀行名を選択または入力してください。';
      if (!details.bankinfo2) newErrors.bankinfo2 = '支店コードを入力してください。';
      if (!details.bankinfo_branch_name) newErrors.bankinfo_branch_name = '支店名を入力してください。';
      if (!isBranchConfirmed) {
        newErrors.isBranchConfirmed = '支店名が正しいか確認し、チェックを入れてください。';
      }
      if (!details.bankinfo3) newErrors.bankinfo3 = '預金種別を選択してください。';
      if (!details.bankinfo4) newErrors.bankinfo4 = '口座番号を入力してください。';
      if (!details.bankinfo5) newErrors.bankinfo5 = '口座名義を入力してください。';
    }
    return newErrors;
  };

  // --- Handlers ---
  const handleAddressSelect = (addr: AddressCandidate) => {
    setDetails(prev => ({ ...prev, prefecture: addr.address1, city: addr.address2, address1: addr.address3 }));
    setIsPopupVisible(false);
    setAddressCandidates([]);
    setErrors(prev => ({
      ...prev,
      prefecture: undefined,
      city: undefined,
      address1: undefined,
    }));
  };

  const handleBankChange = (selectedOption: any) => {
    if (selectedOption) {
      setDetails(prev => ({ 
        ...prev, 
        bankinfo1: selectedOption.label,
        bankinfo2: '', // 支店コードをリセット
        bankinfo_branch_name: '' // 支店名をリセット
      }));
      setSelectedBankCode(selectedOption.value || '');
      setIsBranchAutoFilled(false);
      setIsBranchConfirmed(false);
    } else {
      setDetails(prev => ({ 
        ...prev, 
        bankinfo1: '',
        bankinfo2: '',
        bankinfo_branch_name: ''
      }));
      setSelectedBankCode('');
      setIsBranchAutoFilled(false);
      setIsBranchConfirmed(false);
    }
    if (errors.bankinfo1) {
      setErrors(prev => ({ ...prev, bankinfo1: undefined }));
    }
  };

  const handleBankCreate = (inputValue: string) => {
    setDetails(prev => ({
      ...prev,
      bankinfo1: inputValue,
      bankinfo2: '',
      bankinfo_branch_name: ''
    }));
    setSelectedBankCode('');
    setIsBranchAutoFilled(false);
    setIsBranchConfirmed(false);
    if (errors.bankinfo1) {
      setErrors(prev => ({ ...prev, bankinfo1: undefined }));
    }
  };

  const handleInitialAction = async (actionType: string) => {
    if (!name || !email) {
      setMessage({ type: 'danger', text: '氏名とメールアドレスを入力してください。' });
      return;
    }
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const work_date = `${yyyy}${mm}${dd}`;
    setWorkDate(work_date);

    setIsLoading(true);
    setMessage({ type: '', text: '' });
    setErrors({});
    setMode(actionType);
    setIsConfirming(false); // 初期化
    setIsFinalVerified(false); // 初期化
    try {
      const response = await fetch(`${API_BASE_URL}/api/search-employee`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, work_date }),
      });
      if (!response.ok) throw new Error('サーバーとの通信に失敗しました。');
      const result = await response.json();
      if (result.found) {
        isInitialLoadForFoundUser.current = true;
        const fetchedData = result.data;
        const newDetails: Partial<SubmissionData> = {
          postal_code: fetchedData.postal_code || '',
          prefecture: fetchedData.prefecture || '福岡県',
          city: fetchedData.city || '',
          address1: fetchedData.address1 || '',
          address2: fetchedData.address2 || '',
          bankCheck: fetchedData.bankCheck !== 'FALSE',
          bankinfo1: fetchedData.bankinfo1 || '',
          bankinfo2: fetchedData.bankinfo2 || '',
          bankinfo_branch_name: fetchedData.bankinfo_branch_name || '',
          bankinfo3: fetchedData.bankinfo3 || '普通',
          bankinfo4: fetchedData.bankinfo4 || '',
          bankinfo5: fetchedData.bankinfo5 || '',
          registStatus: true,
        };
        if (actionType === 'complete' && result.has_work_info) {
          newDetails.project_no = fetchedData.project_no || '';
          newDetails.work_time = fetchedData.work_time || '';
          newDetails.work_info = fetchedData.work_info || '';
        }
        setDetails(newDetails);
        setIsBranchConfirmed(!!newDetails.bankinfo_branch_name);

        // 銀行名からコードを復元
        if (newDetails.bankinfo1) {
          const option = bankOptions.find(o => o.label === newDetails.bankinfo1);
          if (option) setSelectedBankCode(option.value);
        }
        
        setMessage({ type: 'info', text: '登録情報が見つかりました。内容を確認・修正してください。' });
      } else {
        setDetails({ registStatus: false, bankCheck: true, prefecture: '福岡県', bankinfo1: '', bankinfo3: '普通' });
        setIsBranchConfirmed(false);
        setMessage({ type: 'warning', text: '登録情報が見つかりませんでした。新規登録として情報を入力してください。\n※登録は初回のみで、2回目以降は入力不要です。' });
      }
      setIsDetailFormVisible(true);
    } catch (error: any) {
      setMessage({ type: 'danger', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 確認画面へ進む前のバリデーション
    if (!isConfirming) {
      const validationErrors = validate();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }
      setErrors({});
      setIsConfirming(true);
      window.scrollTo(0, 0);
      return;
    }

    // 最終送信
    if (!isFinalVerified) {
      setMessage({ type: 'danger', text: '内容に誤りがないことを確認し、チェックを入れてください。' });
      return;
    }

    setIsLoading(true);
    setMessage({ type: '', text: '' });
    const submission: Partial<SubmissionData> = {
      ...details,
      send_select: mode,
      name,
      email,
      work_date: workDate,
      work_content: details.work_info,
      work_time_actual: details.work_time
    };
    try {
      const response = await fetch(`${API_BASE_URL}/api/submit-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submission),
      });
      if (!response.ok) throw new Error('登録に失敗しました。お手数ですが、操作をやり直してください。');
      await response.json();
      setMessage({ type: 'success', text: '登録が完了しました！' });
      setIsDetailFormVisible(false);
      setIsConfirming(false);
      setIsFinalVerified(false);
      setName('');
      setEmail('');
      setDetails({});
      setIsBranchConfirmed(false);
    } catch (error: any) {
      setMessage({ type: 'danger', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const createChangeHandler = (name: keyof SubmissionData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setDetails(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (name === 'bankinfo_branch_name') {
      setIsBranchAutoFilled(false); // 手動入力されたら自動補完フラグをオフにする
      setIsBranchConfirmed(false); // 内容が変わったら再確認
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const createNumericChangeHandler = (name: keyof SubmissionData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/[^0-9]/g, '');
    setDetails(prev => ({ ...prev, [name]: numericValue }));
    if (name === 'bankinfo2') {
      setIsBranchConfirmed(false); // 支店コードが変わったら再確認
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // --- Render ---
  return (
    <div className="container mt-4">
      <h1 className="mb-4">勤怠管理システム</h1>
      {message.text && <div className={`alert alert-${message.type}`} role="alert" style={{ whiteSpace: 'pre-wrap' }}>{message.text}</div>}
      {isLoading && <div className="d-flex justify-content-center my-3"><div className="spinner-border"><span className="visually-hidden">Loading...</span></div></div>}

      <div className="card p-4">
        <div className="mb-3">
          <label htmlFor="name" className="form-label">氏名<span className="text-danger">*</span></label>
          <input type="text" className="form-control" id="name" value={name} onChange={e => setName(e.target.value)} disabled={isLoading || isDetailFormVisible} placeholder="例:トラフィック太郎"/>
        </div>
        <div className="mb-3">
          <label htmlFor="email" className="form-label">メールアドレス<span className="text-danger">*</span></label>
          <input type="email" className="form-control" id="email" value={email} onChange={e => setEmail(e.target.value)} disabled={isLoading || isDetailFormVisible} placeholder="例:sample@email.com"/>
        </div>

        {isDetailFormVisible ? (
          <form onSubmit={handleFinalSubmit} noValidate>
            {isConfirming ? (
              <div className="confirmation-screen">
                <h4 className="mb-4 text-primary border-bottom pb-2">入力内容の確認</h4>
                <div className="alert alert-warning mb-4">
                  まだ送信は完了していません。以下の内容をご確認ください。
                </div>

                <div className="row mb-4">
                  <div className="col-md-6 mb-3">
                    <label className="fw-bold text-muted small">氏名</label>
                    <div className="fs-5">{name}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="fw-bold text-muted small">メールアドレス</label>
                    <div className="fs-5">{email}</div>
                  </div>
                </div>

                <h5 className="mb-3 border-start border-4 border-primary ps-2">住所情報</h5>
                <div className="row mb-4 bg-light p-3 rounded mx-0">
                  <div className="col-md-3 mb-2">
                    <label className="fw-bold text-muted small">郵便番号</label>
                    <div>{details.postal_code}</div>
                  </div>
                  <div className="col-md-9 mb-2">
                    <label className="fw-bold text-muted small">住所</label>
                    <div>{details.prefecture}{details.city}{details.address1} {details.address2}</div>
                  </div>
                </div>

                <h5 className="mb-3 border-start border-4 border-primary ps-2">振込先情報</h5>
                <div className="row mb-4 bg-light p-3 rounded mx-0">
                  <div className="col-md-6 mb-2">
                    <label className="fw-bold text-muted small">銀行名</label>
                    <div>{details.bankinfo1}</div>
                  </div>
                  <div className="col-md-6 mb-2">
                    <label className="fw-bold text-muted small">支店コード / 支店名</label>
                    <div>{details.bankinfo2} {details.bankinfo_branch_name}</div>
                  </div>
                  <div className="col-md-4 mb-2">
                    <label className="fw-bold text-muted small">預金種別</label>
                    <div>{details.bankinfo3}</div>
                  </div>
                  <div className="col-md-4 mb-2">
                    <label className="fw-bold text-muted small">口座番号</label>
                    <div>{details.bankinfo4}</div>
                  </div>
                  <div className="col-md-4 mb-2">
                    <label className="fw-bold text-muted small">口座名義</label>
                    <div>{details.bankinfo5}</div>
                  </div>
                </div>

                {mode === 'complete' && details.registStatus && (
                  <>
                    <h5 className="mb-3 border-start border-4 border-primary ps-2">業務情報</h5>
                    <div className="row mb-4 bg-light p-3 rounded mx-0">
                      <div className="col-md-4 mb-2">
                        <label className="fw-bold text-muted small">プロジェクト番号</label>
                        <div>{details.project_no}</div>
                      </div>
                      <div className="col-md-4 mb-2">
                        <label className="fw-bold text-muted small">業務時間</label>
                        <div>{details.work_time}</div>
                      </div>
                      <div className="col-md-4 mb-2">
                        <label className="fw-bold text-muted small">業務内容</label>
                        <div>{details.work_info}</div>
                      </div>
                    </div>
                  </>
                )}

                <hr className="my-4" />

                <div className="card border-primary mb-4">
                  <div className="card-body">
                    <div className="form-check">
                      <input 
                        className="form-check-input" 
                        type="checkbox" 
                        id="finalVerify" 
                        style={{ width: '1.5em', height: '1.5em' }}
                        checked={isFinalVerified} 
                        onChange={(e) => setIsFinalVerified(e.target.checked)} 
                      />
                      <label className="form-check-label ms-2 fw-bold fs-5" htmlFor="finalVerify">
                        入力内容に誤りがないことを確認しました
                      </label>
                    </div>
                  </div>
                </div>

                <div className="d-grid gap-3 d-md-flex justify-content-md-center mb-4">
                  <button type="button" className="btn btn-outline-secondary btn-lg px-5" onClick={() => setIsConfirming(false)}>
                    修正する
                  </button>
                  <button type="submit" className="btn btn-primary btn-lg px-5" disabled={isLoading || !isFinalVerified}>
                    {isLoading ? '送信中...' : 'この内容で送信する'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h5 className="mb-3">詳細情報</h5>
                <p className="text-muted small">「<span className="text-danger">*</span>」は必須項目です。</p>
                
                <div className="mb-3" ref={popupRef}>
                  <label>郵便番号 (7桁・ハイフンなし)<span className="text-danger">*</span></label>
                  <div className="position-relative">
                    <input type="text" name="postal_code" className={`form-control ${errors.postal_code ? 'is-invalid' : ''}`} value={details.postal_code || ''} onChange={createNumericChangeHandler('postal_code')} maxLength={7} placeholder="例:8100001"/>
                    {isPostalLoading && (
                      <div className="spinner-border spinner-border-sm" role="status" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    )}
                  </div>
                  {errors.postal_code && <div className="invalid-feedback d-block">{errors.postal_code}</div>}
                  {isPopupVisible && addressCandidates.length > 0 && (
                    <div className="list-group position-absolute w-100" style={{ zIndex: 1000 }}>
                      {addressCandidates.map((addr, index) => (
                        <button type="button" key={index} className="list-group-item list-group-item-action" onClick={() => handleAddressSelect(addr)}>
                          {addr.address1}{addr.address2}{addr.address3}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label>都道府県<span className="text-danger">*</span></label>
                  <select name="prefecture" className={`form-select ${errors.prefecture ? 'is-invalid' : ''}`} value={details.prefecture || ''} onChange={createChangeHandler('prefecture')}>
                    {prefectures.map(pref => <option key={pref} value={pref}>{pref}</option>)}
                  </select>
                  {errors.prefecture && <div className="invalid-feedback">{errors.prefecture}</div>}
                </div>

                <div className="mb-3">
                  <label>市区町村<span className="text-danger">*</span></label>
                  <input type="text" name="city" className={`form-control ${errors.city ? 'is-invalid' : ''}`} value={details.city || ''} onChange={createChangeHandler('city')} placeholder="例:福岡市中央区"/>
                  {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                </div>

                <div className="mb-3">
                  <label>町名・番地<span className="text-danger">*</span></label>
                  <input type="text" name="address1" className={`form-control ${errors.address1 ? 'is-invalid' : ''}`} value={details.address1 || ''} onChange={createChangeHandler('address1')} placeholder="例:天神3-10-27"/>
                  {errors.address1 && <div className="invalid-feedback">{errors.address1}</div>}
                </div>

                <div className="mb-3">
                  <label>建物名等</label>
                  <input type="text" name="address2" className="form-control" value={details.address2 || ''} onChange={createChangeHandler('address2')} placeholder="例:天神チクモクビル7階"/>
                </div>
                
                <hr className="my-4" />
                
                <div className="form-check form-switch mb-3 d-none">
                  <input className="form-check-input" type="checkbox" name="bankCheck" id="bankCheck" checked={details.bankCheck === undefined ? true : details.bankCheck} onChange={createChangeHandler('bankCheck')} />
                  <label className="form-check-label" htmlFor="bankCheck">銀行振り込みを希望する</label>
                </div>

                <div>
                  <div className="mb-3">
                    <label>銀行名<span className="text-danger">*</span></label>
                    <CreatableSelect
                      options={bankOptions}
                      value={bankOptions.find(option => option.label === details.bankinfo1) || (details.bankinfo1 ? { label: details.bankinfo1, value: '' } : null)}
                      onChange={handleBankChange}
                      onCreateOption={handleBankCreate}
                      placeholder="選択または銀行名を入力してください"
                      isClearable
                      isSearchable
                      formatCreateLabel={(inputValue) => `「${inputValue}」を直接入力する`}
                      filterOption={createFilter({ ignoreCase: true, ignoreAccents: true, stringify: (option: any) => `${option.label} ${option.value}` })}
                      styles={{ control: (base, state) => ({ ...base, borderColor: errors.bankinfo1 ? '#dc3545' : base.borderColor, '&:hover': { borderColor: errors.bankinfo1 ? '#dc3545' : base.borderColor }})}}
                    />
                    {errors.bankinfo1 && <div className="d-block invalid-feedback">{errors.bankinfo1}</div>}
                    <div className="form-text text-primary">※一覧にない場合は、銀行名を入力して「直接入力する」を選択してください。</div>
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-md">
                      <label className="form-label">支店コード<span className="text-danger">*</span></label>
                      <input type="text" name="bankinfo2" className={`form-control ${errors.bankinfo2 ? 'is-invalid' : ''}`} value={details.bankinfo2 || ''} onChange={createNumericChangeHandler('bankinfo2')} maxLength={3} placeholder="例:123"/>
                      {errors.bankinfo2 && <div className="invalid-feedback">{errors.bankinfo2}</div>}
                    </div>
                    <div className="col-md">
                      <label className="form-label">支店名<span className="text-danger">*</span></label>
                      <input type="text" name="bankinfo_branch_name" className={`form-control ${errors.bankinfo_branch_name ? 'is-invalid' : ''} ${isBranchAutoFilled ? 'is-valid' : ''}`} value={details.bankinfo_branch_name || ''} onChange={createChangeHandler('bankinfo_branch_name')} placeholder="例:トラフィックプラス支店"/>
                      {isBranchAutoFilled && <div className="form-text text-success fw-bold">※支店名を自動取得しました。</div>}
                      {!isBranchAutoFilled && details.bankinfo2?.length === 3 && !details.bankinfo_branch_name && <div className="form-text text-danger">支店名が見つかりませんでした。手動で入力してください。</div>}
                      {errors.bankinfo_branch_name && <div className="invalid-feedback">{errors.bankinfo_branch_name}</div>}
                      
                      {details.bankinfo_branch_name && (
                        <div className="form-check mt-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="confirmBranch"
                            checked={isBranchConfirmed}
                            onChange={(e) => {
                              setIsBranchConfirmed(e.target.checked);
                              if (errors.isBranchConfirmed) {
                                setErrors(prev => ({ ...prev, isBranchConfirmed: undefined }));
                              }
                            }}
                          />
                          <label className="form-check-label text-primary fw-bold" htmlFor="confirmBranch">
                            支店名を確認しました
                          </label>
                          {errors.isBranchConfirmed && <div className="d-block invalid-feedback">{errors.isBranchConfirmed}</div>}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label>預金種別<span className="text-danger">*</span></label>
                    <select name="bankinfo3" className={`form-select ${errors.bankinfo3 ? 'is-invalid' : ''}`} value={details.bankinfo3 || '普通'} onChange={createChangeHandler('bankinfo3')}>
                      <option value="普通">普通</option>
                      <option value="当座">当座</option>
                      <option value="貯蓄">貯蓄</option>
                    </select>
                    {errors.bankinfo3 && <div className="invalid-feedback">{errors.bankinfo3}</div>}
                  </div>

                  <div className="mb-3">
                    <label>口座番号<span className="text-danger">*</span></label>
                    <input type="text" name="bankinfo4" className={`form-control ${errors.bankinfo4 ? 'is-invalid' : ''}`} value={details.bankinfo4 || ''} onChange={createNumericChangeHandler('bankinfo4')} minLength={7} maxLength={8} placeholder="例:1234567"/>
                    <div className="form-text text-primary">※口座番号が7桁に満たない場合は、口座番号の前に0を入力してください。(例:0001234)</div>
                    {errors.bankinfo4 && <div className="invalid-feedback">{errors.bankinfo4}</div>}
                  </div>

                  <div className="mb-3">
                    <label>口座名義<span className="text-danger">*</span></label>
                    <input type="text" name="bankinfo5" className={`form-control ${errors.bankinfo5 ? 'is-invalid' : ''}`} value={details.bankinfo5 || ''} onChange={createChangeHandler('bankinfo5')} placeholder="例:トラフィックタロウ"/>
                    {errors.bankinfo5 && <div className="invalid-feedback">{errors.bankinfo5}</div>}
                    <div className="form-text text-primary">※口座名義はカタカナで入力してください。(例:トラフィックタロウ)</div>
                  </div>
                </div>

                {mode === 'complete' && details.registStatus && (
                  <div>
                    <hr className="my-4" />
                    <h5 className="mb-3">業務情報</h5>
                    <div className="input-group mb-3">
                      <span className="input-group-text" style={{ width: '160px' }}>プロジェクト番号</span>
                      <div className="form-control bg-light">
                        {details.project_no || 'ー'}
                      </div>
                    </div>
                    <div className="input-group mb-3">
                      <span className="input-group-text" style={{ width: '160px' }}>業務時間</span>
                      <div className="form-control bg-light">
                        {details.work_time || 'ー'}
                      </div>
                    </div>
                    <div className="input-group mb-3">
                      <span className="input-group-text" style={{ width: '160px' }}>業務内容</span>
                      <div className="form-control bg-light">
                        {details.work_info || 'ー'}
                      </div>
                    </div>
                  </div>
                )}

                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <button type="button" className="btn btn-secondary" onClick={() => { setIsDetailFormVisible(false); setMessage({ type: '', text: '' }); setErrors({}); }}>戻る</button>
                  <button type="submit" className="btn btn-primary" disabled={isLoading}>確認画面へ進む</button>
                </div>
              </>
            )}
          </form>
        ) : (
          <div className="d-grid gap-2">
            <button className="btn btn-primary" onClick={() => handleInitialAction('regist')} disabled={isLoading || !name || !email}>勤務開始</button>
            <button className="btn btn-success" onClick={() => handleInitialAction('complete')} disabled={isLoading || !name || !email}>勤務終了</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
